import React, { useRef, useCallback, useEffect, useState } from 'react'
import { NumberInput, UI_CAPTION_SEMIBOLD } from 'oolib'
import { SegmentedControl } from '@/components/UI/SegmentedControl'
import { MODE_OPTIONS, getNumberRangeMode, convertNumberRangeValue } from './numberRangeMode'
import {
  StyledSliderContainer,
  StyledSliderTrack,
  StyledSliderRange,
  StyledThumb,
  StyledTrackEndDot,
  StyledValueLabels,
  StyledInputsRow,
  StyledField,
  StyledHistogramContainer,
  StyledHistogramBar,
  StyledHistogramHighlight
} from './styled'

/**
 * NumberRangeSlider - A dual-thumb range slider for selecting a numeric range
 *
 * @param {string} id - Unique identifier for the component
 * @param {Object} value - Current value object with min and max properties { min: number, max: number }
 * @param {Function} onChange - Callback function (id, newValue) => void
 * @param {number} min - Minimum allowed value (default: 0)
 * @param {number} max - Maximum allowed value (default: 100)
 * @param {number} step - Step increment (default: 1)
 * @param {boolean} showInputs - Whether to show number inputs below slider (default: true)
 * @param {boolean} showLabels - Whether to show min/max labels (default: true)
 *
 * Input Behavior:
 * - Users can freely type any value in the text inputs (local state)
 * - The slider and parent always receive clamped/valid values
 * - On blur, the input value is clamped and synced with the slider
 */
const NumberRangeSlider = (props) => {
  const {
    id,
    value,
    onChange,
    min = 0,
    max = 100,
    step = 1,
    showInputs = true,
    showLabels = false,
    showModeToggle = true, // hide when the Value/Range switch is provided elsewhere (e.g. a dropdown outside)
    style,
    aggregatedData // Optional: histogram data from aggrMetricsAcrossDimensions { data: [{count, rangeStart, rangeEnd}, ...] }
  } = props

  const trackRef = useRef(null)
  const [activeThumb, setActiveThumb] = useState(null)
  const [isDragging, setIsDragging] = useState(false)

  // Mode is inferred from the value shape: { exact } => "value", { min, max } => "range".
  // This keeps the sidebar dropdown and this toggle in sync off the same persisted value.
  const mode = getNumberRangeMode(value)

  // Switching mode converts the value shape and persists it via onChange.
  const handleModeChange = (newMode) => {
    if (newMode === mode) return
    onChange && onChange(id, convertNumberRangeValue(value, newMode, min, max))
  }

  // ============================================================
  // LOCAL STATE FOR TEXT INPUTS
  // ============================================================
  // These allow users to freely type any value without immediate validation.
  // The displayed value in the input can temporarily be invalid (e.g., empty, out of bounds).
  const [minInputValue, setMinInputValue] = useState(value?.min ?? min)
  const [maxInputValue, setMaxInputValue] = useState(value?.max ?? max)
  // Single-value entry (used in the "Value" tab)
  const [valueInput, setValueInput] = useState(value?.exact ?? min)

  // Sync text inputs when value prop changes externally (e.g., filter cleared)
  useEffect(() => {
    if (isDragging) return
    setMinInputValue(value?.min ?? min)
    setMaxInputValue(value?.max ?? max)
    setValueInput(value?.exact ?? min)
  }, [value?.min, value?.max, value?.exact, min, max])

  // ============================================================
  // HELPER: CLAMP VALUE TO BOUNDS
  // ============================================================
  // Ensures a value falls within the specified range.
  // If the value is not a valid number, returns the lower bound as fallback.
  const clampValue = (val, lowerBound, upperBound) => {
    const numVal = parseFloat(val)
    if (isNaN(numVal)) return lowerBound
    return Math.min(upperBound, Math.max(lowerBound, numVal))
  }

  // ============================================================
  // CLAMPED VALUES FOR SLIDER
  // ============================================================
  // The slider always uses valid, clamped values for positioning.
  // Even if the input shows "abc" or "999", the slider shows a valid position.
  // Use same fallback logic as input state: undefined/null falls back to min/max bounds
  const sliderMin = clampValue(value?.min ?? min, min, max)
  const sliderMax = clampValue(value?.max ?? max, min, max)


  // ============================================================
  // SLIDER POSITIONING HELPERS
  // ============================================================

  // Convert a numeric value to its percentage position on the track (0-100%)
  const getPercentage = (val) => {
    return ((val - min) / (max - min)) * 100
  }

  // Convert a percentage position (0-100%) back to a numeric value,
  // rounded to the nearest step and clamped to min/max bounds
  const getValueFromPercentage = (percentage) => {
    const rawValue = (percentage / 100) * (max - min) + min
    const steppedValue = Math.round(rawValue / step) * step
    return Math.min(max, Math.max(min, steppedValue))
  }

  // Calculate the value from a mouse/touch X position on the track
  const getValueFromPosition = useCallback((clientX) => {
    if (!trackRef.current) return null

    const trackRect = trackRef.current.getBoundingClientRect()
    const percentage = ((clientX - trackRect.left) / trackRect.width) * 100
    const clampedPercentage = Math.min(100, Math.max(0, percentage))

    return getValueFromPercentage(clampedPercentage)
  }, [min, max, step])

  // ============================================================
  // THUMB DRAG HANDLING
  // ============================================================

  // Called continuously as the user drags a thumb
  const handleThumbMove = useCallback((clientX) => {
    if (!activeThumb || !isDragging) return

    const newValue = getValueFromPosition(clientX)
    if (newValue === null) return

    if (activeThumb === 'min') {
      // Min thumb cannot go past max thumb
      const clampedValue = Math.min(newValue, sliderMax)
      // Update both local input state and parent
      setMinInputValue(clampedValue)
      onChange && onChange(id, { min: clampedValue, max: sliderMax })
    } else {
      // Max thumb cannot go below min thumb
      const clampedValue = Math.max(newValue, sliderMin)
      // Update both local input state and parent
      setMaxInputValue(clampedValue)
      onChange && onChange(id, { min: sliderMin, max: clampedValue })
    }
  }, [activeThumb, isDragging, getValueFromPosition, sliderMin, sliderMax, onChange, id])

  // ============================================================
  // MOUSE/TOUCH EVENT HANDLERS
  // ============================================================

  // Start dragging a thumb
  const handleMouseDown = (thumb) => (e) => {
    e.preventDefault()
    setActiveThumb(thumb)
    setIsDragging(true)
  }

  const handleMouseMove = useCallback((e) => {
    handleThumbMove(e.clientX)
  }, [handleThumbMove])

  const handleTouchMove = useCallback((e) => {
    if (e.touches.length > 0) {
      handleThumbMove(e.touches[0].clientX)
    }
  }, [handleThumbMove])

  // Stop dragging
  const handleMouseUp = useCallback(() => {
    setActiveThumb(null)
    setIsDragging(false)
  }, [])

  // ============================================================
  // TRACK CLICK HANDLING
  // ============================================================
  // When user clicks on the track (not on a thumb), move the closest thumb to that position
  const handleTrackClick = (e) => {
    if (isDragging) return

    const newValue = getValueFromPosition(e.clientX)
    if (newValue === null) return

    // Determine which thumb is closer to the click position
    const distToMin = Math.abs(newValue - sliderMin)
    const distToMax = Math.abs(newValue - sliderMax)

    if (distToMin <= distToMax) {
      // Move min thumb, but don't let it exceed max thumb
      const clampedMin = Math.min(newValue, sliderMax)
      // Update both local input state and parent
      setMinInputValue(clampedMin)
      onChange && onChange(id, { min: clampedMin, max: sliderMax })
    } else {
      // Move max thumb, but don't let it go below min thumb
      const clampedMax = Math.max(newValue, sliderMin)
      // Update both local input state and parent
      setMaxInputValue(clampedMax)
      onChange && onChange(id, { min: sliderMin, max: clampedMax })
    }
  }

  // ============================================================
  // TEXT INPUT CHANGE HANDLERS
  // ============================================================
  // On every keystroke:
  // 1. Update local state (allows free typing)
  // 2. Parse and clamp the value, then send to parent
  //    (parent/slider always gets valid values)

  const handleMinInputChange = (inputId, val) => {
    // Update local display state - user can type anything
    setMinInputValue(val)

    // Parse the value and send clamped version to parent
    const numVal = parseFloat(val)
    if (!isNaN(numVal)) {
      // Clamp: must be >= min and <= current max thumb position
      const clampedMin = Math.min(Math.max(numVal, min), sliderMax)
      onChange && onChange(id, { min: clampedMin, max: sliderMax })
    }
    // If NaN (e.g., empty string), don't update parent - keep previous valid value
  }

  const handleMaxInputChange = (inputId, val) => {
    // Update local display state - user can type anything
    setMaxInputValue(val)

    // Parse the value and send clamped version to parent
    const numVal = parseFloat(val)
    if (!isNaN(numVal)) {
      // Clamp: must be <= max and >= current min thumb position
      const clampedMax = Math.max(Math.min(numVal, max), sliderMin)
      onChange && onChange(id, { min: sliderMin, max: clampedMax })
    }
    // If NaN (e.g., empty string), don't update parent - keep previous valid value
  }

  // ============================================================
  // TEXT INPUT BLUR HANDLERS
  // ============================================================
  // On blur, sync the displayed input value with the actual clamped value.
  // This "corrects" any invalid input the user may have typed.

  const handleMinBlur = () => {
    const numVal = parseFloat(minInputValue)
    if (isNaN(numVal)) {
      // Invalid input - reset display to current slider value
      setMinInputValue(sliderMin)
    } else {
      // Valid input - clamp and update both display and parent
      const clamped = Math.min(Math.max(numVal, min), sliderMax)
      setMinInputValue(clamped)
      onChange && onChange(id, { min: clamped, max: sliderMax })
    }
  }

  const handleMaxBlur = () => {
    const numVal = parseFloat(maxInputValue)
    if (isNaN(numVal)) {
      // Invalid input - reset display to current slider value
      setMaxInputValue(sliderMax)
    } else {
      // Valid input - clamp and update both display and parent
      const clamped = Math.max(Math.min(numVal, max), sliderMin)
      setMaxInputValue(clamped)
      onChange && onChange(id, { min: sliderMin, max: clamped })
    }
  }

  // ============================================================
  // SINGLE VALUE ("Value" tab) HANDLERS
  // ============================================================
  // The "Value" tab selects a single exact value, persisted as { exact }
  // (matching the app's numberRangeType data model).

  const handleValueInputChange = (inputId, val) => {
    setValueInput(val)

    const numVal = parseFloat(val)
    if (!isNaN(numVal)) {
      const clamped = Math.min(Math.max(numVal, min), max)
      onChange && onChange(id, { exact: clamped })
    }
  }

  const handleValueBlur = () => {
    const numVal = parseFloat(valueInput)
    if (isNaN(numVal)) {
      // Invalid input - reset display to current value
      setValueInput(value?.exact ?? min)
    } else {
      const clamped = Math.min(Math.max(numVal, min), max)
      setValueInput(clamped)
      onChange && onChange(id, { exact: clamped })
    }
  }

  // ============================================================
  // GLOBAL EVENT LISTENERS FOR DRAG
  // ============================================================
  // Attach/detach global listeners so dragging works even when mouse leaves the thumb
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove)
      document.addEventListener('touchend', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove])

  // ============================================================
  // HISTOGRAM DATA PROCESSING
  // ============================================================
  // Process aggregatedData to render histogram bars
  const histogramBars = React.useMemo(() => {
    if (!aggregatedData?.data || aggregatedData.data.length === 0) return null

    // Sort by sortOrder (lowest range to highest)
    const sortedData = [...aggregatedData.data].sort((a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
    )

    const maxCount = Math.max(...sortedData.map(d => d.count || 0))
    if (maxCount === 0) return null

    return sortedData.map((bucket, index) => {
      const count = bucket.count || 0
      const heightPercent = (count / maxCount) * 100

      return {
        key: index,
        heightPercent
      }
    })
  }, [aggregatedData])

  // ============================================================
  // RENDER
  // ============================================================
  const minPercent = getPercentage(sliderMin)
  const maxPercent = getPercentage(sliderMax)

  return (
    <StyledSliderContainer style={style} id={id}>
      {/* Value / Range mode toggle */}
      {showModeToggle && (
        <SegmentedControl
          style={{ marginBottom: '1rem' }}
          options={MODE_OPTIONS}
          value={mode}
          onChange={handleModeChange}
        />
      )}

      {/* Histogram bars with highlight overlay */}
      {mode === 'range' && histogramBars && (
        <StyledHistogramContainer>
          {histogramBars.map(bar => (
            <StyledHistogramBar
              key={bar.key}
              style={{ height: `${bar.heightPercent}%` }}
            />
          ))}
          {/* Highlight overlay positioned based on slider thumbs */}
          <StyledHistogramHighlight
            style={{
              left: `${minPercent}%`,
              right: `${100 - maxPercent}%`
            }}
          />
        </StyledHistogramContainer>
      )}

      {/* Slider Track */}
      {mode === 'range' && (
      <StyledSliderTrack ref={trackRef} onClick={handleTrackClick}>
        {/* Right end dot (left dot is via ::after pseudo-element) */}
        <StyledTrackEndDot />

        {/* Filled range between the two thumbs */}
        <StyledSliderRange
          style={{
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`
          }}
        />

        {/* Min thumb (left) */}
        <StyledThumb
          role="slider"
          tabIndex={0}
          aria-valuemin={min}
          aria-valuemax={sliderMax}
          aria-valuenow={sliderMin}
          aria-label="Minimum value"
          style={{ left: `${minPercent}%` }}
          $isActive={activeThumb === 'min'}
          onMouseDown={handleMouseDown('min')}
          onTouchStart={handleMouseDown('min')}
        />

        {/* Max thumb (right) */}
        <StyledThumb
          role="slider"
          tabIndex={0}
          aria-valuemin={sliderMin}
          aria-valuemax={max}
          aria-valuenow={sliderMax}
          aria-label="Maximum value"
          style={{ left: `${maxPercent}%` }}
          $isActive={activeThumb === 'max'}
          onMouseDown={handleMouseDown('max')}
          onTouchStart={handleMouseDown('max')}
        />
      </StyledSliderTrack>
      )}

      {/* Selected min/max values at the ends of the slider */}
      {mode === 'range' && showLabels && (
        <StyledValueLabels>
          <UI_CAPTION_SEMIBOLD>{String(sliderMin)}</UI_CAPTION_SEMIBOLD>
          <UI_CAPTION_SEMIBOLD>{String(sliderMax)}</UI_CAPTION_SEMIBOLD>
        </StyledValueLabels>
      )}

      {/* Number inputs for precise value entry (Range tab) */}
      {mode === 'range' && showInputs && (
        <StyledInputsRow>
          <StyledField>
            <UI_CAPTION_SEMIBOLD>FROM</UI_CAPTION_SEMIBOLD>
            <NumberInput
              placeholder="Min"
              value={minInputValue}
              onChange={handleMinInputChange}
              onBlur={handleMinBlur}
            />
          </StyledField>
          <StyledField>
            <UI_CAPTION_SEMIBOLD>TO</UI_CAPTION_SEMIBOLD>
            <NumberInput
              placeholder="Max"
              value={maxInputValue}
              onChange={handleMaxInputChange}
              onBlur={handleMaxBlur}
            />
          </StyledField>
        </StyledInputsRow>
      )}

      {/* Single value entry (Value tab) */}
      {mode === 'value' && (
        <StyledInputsRow>
          <StyledField>
            <NumberInput
              placeholder="Value"
              value={valueInput}
              onChange={handleValueInputChange}
              onBlur={handleValueBlur}
            />
          </StyledField>
        </StyledInputsRow>
      )}
    </StyledSliderContainer>
  )
}

export default NumberRangeSlider
